import React, { lazy, Suspense, useEffect, useState, useMemo, useRef, forwardRef, useImperativeHandle } from 'react'
import classNames from 'classnames'

import constants from '@/constants'
import { QUESTION_STATUS } from '@/constants/marketBackendEnums'
import { selectMarketById, selectOrderBookByOutcomeId } from '@/store/markets'
import { i18n } from '@/locale/i18n'
import { useActiveCurrency, useCurrencies, useCurrentUser } from '@/hooks'
import useOrderBookRealTime from '@/hooks/useOrderBookRealTime'
import useIsMobileSize from '@/hooks/useIsMobileSize'
import { useDispatch, useSelector } from 'react-redux'
import {
  clearLastBet,
  fetchQuestionBetHistory,
  resetBetHistory,
  selectBetHistory,
  selectBetHistoryPagination,
  selectBetHistoryStatus,
  selectWagerSelected,
  selectUserOrders,
} from '@/store/wagers'
import { fetchOrderBook } from '@/store/markets'
import './Market.scss'
import MarketCard from '../market-card/MarketCard' 
import ErrorHandler from '@/components/ui/error-handler'
import TabbedContent from '@/components/tabbed-content/TabbedContent'
import { selectCommentsPagination, selectCommentsScopedTo } from '@/store/comments'
import { deriveMarketUi } from '../utils/marketType'

const QuestionSummary = lazy(() => import('@/features/market/question-summary'))
const CommentPanel = lazy(() => import('@/features/comments/CommentPanel'))
const BetHistory = lazy(() => import('@/features/bet-history'))
const RelatedMarkets = lazy(() => import('@/features/market/related-markets'))
import OrderBook from '../order-book'

// Add shimmer animation styles
const shimmerStyles = `
  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`

// Inject styles if not already present
if (typeof document !== 'undefined' && !document.getElementById('market-shimmer-styles')) {
  const style = document.createElement('style')
  style.id = 'market-shimmer-styles'
  style.textContent = shimmerStyles
  document.head.appendChild(style)
}

// Shimmer effect component
const ShimmerEffect = ({ className = '', children }) => {
  return (
    <div className={`relative overflow-hidden ${className}`}>
      {children}
      <div
        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
        style={{
          animation: 'shimmer 2s infinite',
          transform: 'translateX(-100%)',
        }}></div>
    </div>
  )
}

const DYNAMIC_IMPORT_FETCH_FAILED = 'failed to fetch dynamically imported module'

// Error fallback for components
const ComponentErrorFallback = ({ componentName, onRetry, error }) => {
  useEffect(() => {
    const msg = error?.message ?? ''
    if (msg.toLowerCase().includes(DYNAMIC_IMPORT_FETCH_FAILED)) {
      window.location.reload()
    }
  }, [error])

  return (
    <div className="flex flex-col items-center justify-center p-6 bg-surface-2 border border-dashed border-Neutral-3 rounded-lg mb-4">
      <div className="w-12 h-12 bg-negative-2 rounded-full flex items-center justify-center mb-3">
        <svg className="w-6 h-6 text-negative-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
      </div>
      <div className="text-center space-y-2">
        <div className="text-Neutral-6 text-sm font-medium">
          {i18n('common.failed_to_load', { name: componentName })}
        </div>
        <div className="text-Neutral-4 text-xs">{error?.message || i18n('common.something_went_wrong')}</div>
        {onRetry && (
          <button
            onClick={onRetry}
            className="mt-3 px-4 py-2 text-xs bg-[var(--color-brand-04)] text-[var(--color-brand-01)] rounded-lg hover:bg-[var(--color-brand-05)] transition-colors">
            Try Again
          </button>
        )}
      </div>
    </div>
  )
}

// Enhanced skeleton loaders

const SummarySkeleton = () => (
  <ShimmerEffect className="p-6 rounded-lg bg-surface-2 border border-Neutral-3">
    <div className="animate-pulse space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 bg-surface-3 rounded-full"></div>
        <div className="h-6 bg-surface-3 rounded w-32"></div>
      </div>

      {/* Content lines */}
      <div className="space-y-3">
        <div className="h-4 bg-surface-3 rounded w-full"></div>
        <div className="h-4 bg-surface-3 rounded w-5/6"></div>
        <div className="h-4 bg-surface-3 rounded w-4/6"></div>
        <div className="h-4 bg-surface-3 rounded w-3/4"></div>
      </div>

      {/* Tags or metadata */}
      <div className="flex gap-2 mt-4">
        <div className="h-6 bg-surface-3 rounded-full w-16"></div>
        <div className="h-6 bg-surface-3 rounded-full w-20"></div>
      </div>
    </div>
  </ShimmerEffect>
)

const CommentSkeleton = () => (
  <ShimmerEffect className="p-6 rounded-lg bg-surface-2 border border-Neutral-3">
    <div className="animate-pulse space-y-6">
      {/* Comment input */}
      <div className="space-y-3">
        <div className="h-5 bg-surface-3 rounded w-24"></div>
        <div className="h-12 bg-surface-3 rounded-lg w-full"></div>
        <div className="flex justify-end">
          <div className="h-8 bg-surface-3 rounded w-20"></div>
        </div>
      </div>

      {/* Comments list */}
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex items-start gap-3">
            <div className="w-10 h-10 bg-surface-3 rounded-full flex-shrink-0"></div>
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <div className="h-4 bg-surface-3 rounded w-20"></div>
                <div className="h-3 bg-surface-3 rounded w-16"></div>
              </div>
              <div className="space-y-1">
                <div className="h-4 bg-surface-3 rounded w-full"></div>
                <div className="h-4 bg-surface-3 rounded w-3/4"></div>
              </div>
              <div className="flex gap-4">
                <div className="h-3 bg-surface-3 rounded w-12"></div>
                <div className="h-3 bg-surface-3 rounded w-12"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </ShimmerEffect>
)

const BetHistorySkeleton = () => (
  <ShimmerEffect className="p-6 rounded-lg bg-surface-2 border border-Neutral-3">
    <div className="animate-pulse space-y-6">
      {/* Header with filters */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="h-6 bg-surface-3 rounded w-32"></div>
          <div className="h-6 bg-surface-3 rounded-full w-8"></div>
        </div>
        <div className="flex gap-2">
          <div className="h-8 bg-surface-3 rounded w-20"></div>
          <div className="h-8 bg-surface-3 rounded w-16"></div>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2">
        <div className="h-8 bg-surface-3 rounded-full w-16"></div>
        <div className="h-8 bg-surface-3 rounded-full w-20"></div>
        <div className="h-8 bg-surface-3 rounded-full w-18"></div>
      </div>

      {/* Bet entries */}
      <div className="space-y-3">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="flex justify-between items-center p-4 border border-Neutral-3 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-surface-3 rounded-full"></div>
              <div className="space-y-1">
                <div className="h-4 bg-surface-3 rounded w-24"></div>
                <div className="h-3 bg-surface-3 rounded w-20"></div>
              </div>
            </div>
            <div className="text-right space-y-1">
              <div className="h-5 bg-surface-3 rounded w-16"></div>
              <div className="h-3 bg-surface-3 rounded w-12"></div>
            </div>
          </div>
        ))}
      </div>

      {/* Load more */}
      <div className="flex justify-center">
        <div className="h-10 bg-surface-3 rounded w-24"></div>
      </div>
    </div>
  </ShimmerEffect>
)

// Preload components function
const preloadComponents = () => {
  // Preload all lazy-loaded components
  import('@/features/market/question-summary')
  import('@/features/comments/CommentPanel')
  import('@/features/bet-history')
  import('@/features/market/compact-question')
}

/** Small pages keep the market page light; the anchor pulls the next one while scrolling. */
const BET_HISTORY_PAGE_SIZE = 10

const Market = forwardRef((props, ref) => {
  const currencies = useCurrencies().currencies
  const {
    categories,
    currentOutcomeSelection,
    inlineBehavior,
    isModalComponent,
    showChart,
    showComments,
    showOrderBook,
    showLatestBets,
    showMakeBet,
    showSummary,
    showRelatedMarket,
    className = '',
    isSelectedToBet = false,
    inMarketPage = false,
    showSelectedOutcome,
    isRelatedMarket,
    onOutcomeSelected,
    position,
    setPosition,
    // PlaceBet orderbook integration props
    onOpenOrderbook,
    onScrollToOrderbook,
    onSelectOrderbookPrice,
    onPriceClick,
    onSetActiveTab = () => {},
  } = props
  const dispatch = useDispatch()
  const questionId = (props.question && props.question.id) || props.questionId
  const question = props.question || useSelector(selectMarketById(questionId))
  const latestBets = useSelector(selectBetHistory(questionId))
  const latestBetsStatus = useSelector(selectBetHistoryStatus(questionId))
  const latestBetsPagination = useSelector(selectBetHistoryPagination(questionId))
  const commentsPagination = useSelector(selectCommentsPagination)
  const commentsScopedTo = useSelector(selectCommentsScopedTo)

  const activeCurrency = useActiveCurrency()
  const currentUser = useCurrentUser()
  const wagerSelected = useSelector(selectWagerSelected())
  const userOrders = useSelector(selectUserOrders()) || []
  const isMobileSize = useIsMobileSize()

  // Memoize outcome ID selection for orderbook to prevent unnecessary selector recalculations
  const selectedOutcomeId = useMemo(() => {
    // Use currentOutcomeSelection if available
    if (currentOutcomeSelection?.id) return currentOutcomeSelection.id

    // Use wagerSelected outcome if available
    if (wagerSelected?.outcome?.id) return wagerSelected.outcome.id

    // On mobile, default to first outcome ID if no selection
    if (isMobileSize && question?.outcomes?.length > 0) {
      return question.outcomes[0].id
    }

    return undefined
  }, [currentOutcomeSelection?.id, wagerSelected?.outcome?.id, isMobileSize, question?.outcomes?.length])

  // Get orderbook data using memoized outcome ID
  const orderbook = useSelector(selectOrderBookByOutcomeId(selectedOutcomeId, activeCurrency?.id, position))
  const [orderBookOpen, setOrderBookOpen] = useState(false)
  const [betListActiveTabIndex, setBetListActiveTabIndex] = useState(null)
  // Bumped on every external tab request so a repeat request for the tab that is already
  // active still reaches the BetList.
  const [betListTabRequestId, setBetListTabRequestId] = useState(0)
  const marketCardRef = useRef(null)
  const orderBookRef = useRef(null)

  const [belowFoldReady, setBelowFoldReady] = useState(false)
  const [betHistoryFilters, setBetHistoryFilters] = useState({})
  const [activityTabIndex, setActivityTabIndex] = useState(0)

  const showBetHistoryTab = showLatestBets && question?.status !== QUESTION_STATUS.CANCELLED
  const isBetHistoryTabActive = showBetHistoryTab && activityTabIndex === 0
  // The comments store is shared with the platform-wide live strip, so its total only
  // describes this market once a fetch for this market has landed; until then the
  // market payload carries the count.
  const commentsCountIsForThisMarket = question && commentsScopedTo === String(question.id)

  const isRealMoney = activeCurrency && activeCurrency?.id !== currencies.OOM.id
  const availableMarket = (question && !isRealMoney) || (isRealMoney && question.real_currency_available)

  // Subscribe to real-time order book updates
  const orderBookSubscriptions = useMemo(() => {
    const currency = activeCurrency && activeCurrency?.id === 'OOM' ? 'OOM' : question?.canonical_currency

    if (!question?.id || !currency || !selectedOutcomeId || !position) {
      return []
    }

    return [
      {
        marketId: question.id,
        currency: currency,
        outcome: selectedOutcomeId,
        positionType: position,
      },
    ]
  }, [question?.id, activeCurrency?.id, selectedOutcomeId, position])

  useOrderBookRealTime(orderBookSubscriptions)

  // Expose orderbook control functions and chart refresh via ref
  useImperativeHandle(
    ref,
    () => ({
      openOrderbook: () => {
        setOrderBookOpen(true)
      },
      scrollToOrderbook: () => {
        if (orderBookRef.current) {
          // Add top offset for desktop to align with PlaceBet component
          const topOffset = isMobileSize ? 0 : -180 // 180px offset for desktop

          const elementTop = orderBookRef.current.getBoundingClientRect().top + window.scrollY
          const offsetPosition = elementTop + topOffset

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          })
        }
      },
      selectOrderbookPrice: price => {
        // Additional logic to highlight the price in orderbook could go here
      },
      refreshChart: newOutcomes => {
        if (marketCardRef.current?.refreshChart) {
          marketCardRef.current.refreshChart(newOutcomes)
        }
      },
    }),
    [isMobileSize]
  )

  // Preload components when the market is loaded
  // useEffect(() => {
  //   if (question) {
  //     preloadComponents()
  //   }
  // }, [question])

  // When bet result is hidden, clear showMakeBet state
  useEffect(() => {
    if (showMakeBet) {
      dispatch(clearLastBet())
    }
  }, [showMakeBet])

  useEffect(() => {
    if (belowFoldReady) return

    if (typeof window === 'undefined') {
      setBelowFoldReady(true)
      return
    }

    let resolved = false
    const markReady = () => {
      if (!resolved) {
        resolved = true
        setBelowFoldReady(true)
      }
    }

    let idleId = null
    let timeoutId = null

    if ('requestIdleCallback' in window && typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(markReady, { timeout: 1500 })
    } else {
      timeoutId = window.setTimeout(markReady, 1500)
    }

    window.addEventListener('pointerdown', markReady, { once: true, passive: true })
    window.addEventListener('scroll', markReady, { once: true, passive: true })

    return () => {
      resolved = true
      if (idleId !== null && 'cancelIdleCallback' in window && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId)
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
      }
      window.removeEventListener('pointerdown', markReady)
      window.removeEventListener('scroll', markReady)
    }
  }, [belowFoldReady])

  useEffect(() => {
    if (orderBookOpen && !belowFoldReady) {
      setBelowFoldReady(true)
    }
  }, [orderBookOpen, belowFoldReady])

  // Opening the tab, changing market or changing filters all start the list from the first
  // page: `noOffset` because the pagination read here describes the run being discarded.
  useEffect(() => {
    if (!belowFoldReady || !isBetHistoryTabActive) return
    loadNextLatestBets(true, true)
  }, [questionId, belowFoldReady, isBetHistoryTabActive, betHistoryFilters])

  // A shared-comment deep link has to land on the comments tab.
  useEffect(() => {
    if (typeof window === 'undefined' || !showComments) return
    if (!window.location.search.includes('shared_comment_id=')) return
    setActivityTabIndex(showBetHistoryTab ? 1 : 0)
  }, [showComments, showBetHistoryTab])

  // A complete version of a given market should be loaded
  useEffect(() => {
    if (showMakeBet || showSummary) {
      if (!questionId) {
        throw new Error("question or questionId parameter weren't provided to <Question> component")
      }
    }
  }, [questionId])

  useEffect(() => {
    if (!question?.id || !question.order_book_enabled || !selectedOutcomeId || !position) return
    if (!belowFoldReady && !orderBookOpen) return

    dispatch(
      fetchOrderBook({
        question: question.id,
        currency_mode: activeCurrency?.id === 'OOM' ? 'play_money' : 'real_money',
        outcome: selectedOutcomeId,
        position,
        source: 'market_page',
        showErrorToast: orderBookOpen,
      })
    )
  }, [
    question?.id,
    question?.order_book_enabled,
    selectedOutcomeId,
    position,
    activeCurrency?.id,
    belowFoldReady,
    orderBookOpen,
  ])

  // TODO: Latest bets / bet history

  const loadNextLatestBets = (noOffset = false, clearOldBets = false) => {
    if (!question?.id) return

    const requestParams = {
      question: question.id,
      currency_mode: betHistoryFilters.currency_mode || '',
      search: betHistoryFilters.search || '',
      startIndex: latestBetsPagination && !noOffset ? latestBetsPagination.offset + BET_HISTORY_PAGE_SIZE : 0,
      limit: BET_HISTORY_PAGE_SIZE,
    }

    if (betHistoryFilters.following) requestParams.following = 1
    if (betHistoryFilters.my_bets) requestParams.my_bets = 1
    if (clearOldBets) dispatch(resetBetHistory({ questionId: question.id }))
    dispatch(fetchQuestionBetHistory(question, requestParams, clearOldBets))
  }

  // Function to handle info icon click from OrderBook
  const handleOrderBookInfoClick = () => {
    // Set the active tab to open orders (index 2)
    setBetListActiveTabIndex(2)
    setBetListTabRequestId(previous => previous + 1)

    // Scroll to the MarketCard which contains the BetList
    if (marketCardRef.current) {
      marketCardRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }

  const renderQuestionSummary = question => {
    if (question.description || question.tags?.length)
      return (
        <ErrorHandler
          component="Market.QuestionSummary"
          fallback={({ error, resetErrorBoundary }) => (
            <ComponentErrorFallback componentName="Question Summary" error={error} onRetry={resetErrorBoundary} />
          )}>
          <div className="question-card__section question-card__section--with-divider question-card__summary justify-center">
            <Suspense fallback={<SummarySkeleton />}>
              <QuestionSummary question={question} categories={categories} />
            </Suspense>
          </div>
        </ErrorHandler>
      )
  }

  const renderLatestBets = () => {
    if (!belowFoldReady) {
      return (
        <div className="question-card__latest-bets relative z-[2]">
          <BetHistorySkeleton />
        </div>
      )
    }
    return (
      <ErrorHandler
        component="Market.BetHistory"
        fallback={({ error, resetErrorBoundary }) => (
          <ComponentErrorFallback componentName="Bet History" error={error} onRetry={resetErrorBoundary} />
        )}>
        <div className="question-card__latest-bets relative z-[2]">
          <Suspense fallback={<BetHistorySkeleton />}>
            <BetHistory
              bets={latestBets}
              type="question"
              me={currentUser}
              loading={latestBetsStatus === constants.LOADING && latestBets.length === 0}
              loadingMore={latestBetsStatus === constants.LOADING}
              filters={betHistoryFilters}
              latestBetsPagination={latestBetsPagination}
              onChangeFilter={updateFilters => setBetHistoryFilters(updateFilters)}
              onLoadMore={() => {
                loadNextLatestBets()
              }}
            />
          </Suspense>
        </div>
      </ErrorHandler>
    )
  }

  const renderComments = () => {
    if (!belowFoldReady) {
      return <CommentSkeleton />
    }

    return (
      <ErrorHandler
        component="Market.Comments"
        fallback={({ error, resetErrorBoundary }) => (
          <ComponentErrorFallback componentName="Comments" error={error} onRetry={resetErrorBoundary} />
        )}>
        <Suspense fallback={<CommentSkeleton />}>
          <CommentPanel marketId={question.id} hideHeader />
        </Suspense>
      </ErrorHandler>
    )
  }

  const tabCount = value => <span className="question-card__tabed-content--count">{value || 0}</span>

  // Bet history and comments share the last block of the page: only the active tab mounts,
  // so the inactive list never fetches or renders until the user asks for it.
  const renderActivityTabs = () => {
    const contents = []

    if (showBetHistoryTab) {
      contents.push({
        label: (
          <span className="flex items-center">
            {i18n('question_card.bet_history')}
            {tabCount(latestBetsPagination?.total)}
          </span>
        ),
        render: renderLatestBets,
      })
    }

    if (showComments) {
      contents.push({
        label: (
          <span className="flex items-center">
            {i18n('question_card.comments')}
            {tabCount(commentsCountIsForThisMarket ? commentsPagination?.total : question.comment_count)}
          </span>
        ),
        render: renderComments,
      })
    }

    if (!contents.length) return null

    return (
      <div className="question-card__section question-card__tabed-content question-card__activity">
        <TabbedContent
          className="question-card__activity-tabs"
          contents={contents}
          activeIndex={Math.min(activityTabIndex, contents.length - 1)}
          onContentChange={setActivityTabIndex}
        />
      </div>
    )
  }

  const marketUi = useMemo(() => deriveMarketUi(question), [
    question?.markets_correlation,
    question?.outcomes?.length,
    question?.outcomes?.[0]?.position_labels,
    question?.outcomes?.[1]?.position_labels,
  ])

  const renderOrderBook = question => {
    // Always render OrderBook so the header (title, info icon, dropdown) is visible from the start
    return (
      <ErrorHandler
        component="Market.OrderBook"
        fallback={({ error, resetErrorBoundary }) => (
          <ComponentErrorFallback componentName="Order Book" error={error} onRetry={resetErrorBoundary} />
        )}>
        <div
          className={classNames(
            'question-card__orderbook question-card__section flex flex-col gap-8',
            inMarketPage ? 'mt-0' : 'mt-0 md:mt-4 border border-solid border-Neutral-3'
          )}
          ref={orderBookRef}>
          <OrderBook
            question={question}
            currency={activeCurrency}
            orders={orderbook}
            isLoading={orderbook.status === constants.LOADING}
            spread={orderbook.spread}
            outcome={currentOutcomeSelection}
            positionType={position}
            setOutcome={onOutcomeSelected}
            setPositionType={setPosition}
            onChangeOutcome={onOutcomeSelected}
            open={belowFoldReady ? orderBookOpen : false}
            setOpen={setOrderBookOpen}
            onInfoClick={handleOrderBookInfoClick}
            wagerSelected={wagerSelected}
            userOrders={userOrders}
            onOpenOrderbook={onOpenOrderbook}
            onScrollToOrderbook={onScrollToOrderbook}
            onSelectOrderbookPrice={onSelectOrderbookPrice}
            isMarketPage={inMarketPage}
            onPriceClick={onPriceClick}
            onSetActiveTab={onSetActiveTab}
            selectorType={marketUi.orderBook.selectorType}
            showTabs={marketUi.orderBook.showTabs}
          />
        </div>
      </ErrorHandler>
    )
  }

  const render = () => {
    if (question == null) {
      return (
        <ShimmerEffect className="question-card bg-surface-2 rounded-lg border border-Neutral-3 overflow-hidden">
          <article className="animate-pulse">
            <div className="p-4 space-y-4">
              <div className="space-y-3">
                {/* Categories */}
                <div className="flex gap-2">
                  <div className="h-6 bg-surface-3 rounded-full w-16"></div>
                  <div className="h-6 bg-surface-3 rounded-full w-20"></div>
                </div>

                {/* Title */}
                <div className="space-y-2">
                  <div className="h-6 bg-surface-3 rounded w-full"></div>
                  <div className="h-6 bg-surface-3 rounded w-3/4"></div>
                </div>

                {/* Metadata */}
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-surface-3 rounded"></div>
                    <div className="h-4 bg-surface-3 rounded w-24"></div>
                  </div>
                  <div className="h-4 bg-surface-3 rounded w-16"></div>
                </div>
              </div>

              {/* Outcomes */}
              <div className="flex flex-col gap-2">
                {[1, 2].map(i => (
                  <div
                    key={i}
                    className="flex justify-between items-center p-3 bg-surface-2 border border-Neutral-3 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-surface-3 rounded-full"></div>
                      <div className="space-y-1">
                        <div className="h-4 bg-surface-3 rounded w-20"></div>
                        <div className="h-3 bg-surface-3 rounded w-16"></div>
                      </div>
                    </div>
                    <div className="text-right space-y-1">
                      <div className="h-5 bg-surface-3 rounded w-12"></div>
                      <div className="h-3 bg-surface-3 rounded w-8"></div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="flex justify-between items-center py-3 border-t border-Neutral-3">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 bg-surface-3 rounded"></div>
                  <div className="h-4 bg-surface-3 rounded w-20"></div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-4 bg-surface-3 rounded w-16"></div>
                  <div className="w-4 h-4 bg-surface-3 rounded"></div>
                </div>
              </div>
            </div>
          </article>
        </ShimmerEffect>
      )
    }
    const betList = props.betList || question.wagers || []

    // const showMakeBet = shouldRenderMakeBet(betList)
    const showBetStatus = props.showBetStatus && betList.length && betList.length > 0
    const classList = classNames('question-card', className, {
      'question-card--show-bet-status': showBetStatus,
      'question-card--is-modal': isModalComponent,
      'question-card--is-selected': isSelectedToBet,
    })

    return (
      <article className={classList} ref={ref}>
        <ErrorHandler
          component="Market.MarketCard"
          fallback={({ error, resetErrorBoundary }) => (
            <ComponentErrorFallback componentName="Market Card" error={error} onRetry={resetErrorBoundary} />
          )}>
          <MarketCard
            id={'market-card-' + question.id}
            question={question}
            categories={categories}
            currentOutcomeSelection={currentOutcomeSelection}
            position={position}
            showSummary={showSummary}
            showChart={showChart}
            inlineBehavior={inlineBehavior}
            className={className}
            showSelectedOutcome={showSelectedOutcome}
            isRelatedMarket={isRelatedMarket}
            onOutcomeSelected={onOutcomeSelected}
            betList={props.betlist}
            inMarketPage={inMarketPage}
            loadNextLatestBets={loadNextLatestBets}
            ref={marketCardRef}
            betListActiveTabIndex={betListActiveTabIndex}
            betListTabRequestId={betListTabRequestId}
            onBetListTabChange={() => setBetListActiveTabIndex(null)}
            currentUser={currentUser}
            // Pass orderbook control props
            onOpenOrderbook={onOpenOrderbook}
            onScrollToOrderbook={onScrollToOrderbook}
            onSelectOrderbookPrice={onSelectOrderbookPrice}
            onPriceClick={onPriceClick}
            onSetActiveTab={onSetActiveTab}
            renderAfterMarketCardSlot={showOrderBook && marketUi.orderBook.location === 'belowCard' && renderOrderBook(question)}
          />
        </ErrorHandler>

        {showSummary && renderQuestionSummary(question)}

        {showRelatedMarket && belowFoldReady && (
          <div className="question-card__section question-card__related-markets">
            <Suspense fallback={null}>
              <RelatedMarkets questionId={questionId} />
            </Suspense>
          </div>
        )}

        {renderActivityTabs()}
      </article>
    )
  }

  return render()
})

export default React.memo(Market)
